import * as React from "react";
import { WandSparkles } from "lucide-react";
import { DetectedPhraseAlert } from "@/components/DetectedPhraseAlert";
import { SocoAiLogo } from "@/components/Holo";
import { usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { Button, Skeleton, Spinner, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useComplianceChecker, useSocialCoachSession } from "@/utils";
import { ShareCaption } from "./hooks/useShareCaption";

/** The caption box while the first caption is being written: the label, the AI mark, and shimmer lines. */
export const CaptionWriting: React.FC<{ label?: string; className?: string }> = ({
  label = "Writing your caption…",
  className,
}) => (
  <div
    className={cn("flex flex-col gap-2 rounded-md border border-slate-200 p-3", className)}
    role="status"
    data-testid="caption-writing"
  >
    <div className="flex items-center gap-1.5 text-sm font-medium text-slate-500">
      {label}
      <SocoAiLogo variant="mono" height={12} />
    </div>
    <Skeleton className="h-3.5 w-[92%]" />
    <Skeleton className="h-3.5 w-[85%]" />
    <Skeleton className="h-3.5 w-[60%]" />
  </div>
);

/** Laid over a caption that's being rewritten; the old text stays dimmed underneath. */
export const CaptionRewriting: React.FC = () => (
  <div
    className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-md bg-white/60 text-sm font-medium text-slate-600"
    role="status"
    data-testid="caption-rewriting"
  >
    <Spinner className="size-4 text-primary" />
    Rewriting your caption…
    <SocoAiLogo variant="mono" height={12} />
  </div>
);

export interface CaptionFieldProps {
  /**
   * A caption the flow writes: from the reviews (review designs) or from the design (an unpublished
   * design, which has no prompt caption yet). Absent for designs that post the prompt's caption.
   */
  caption?: ShareCaption;
  /** Other designs: opens Caption Coach, when the coach allows it. */
  onOpenCaptionCoach?: () => void;
  disabled?: boolean;
}

export const CaptionField: React.FC<CaptionFieldProps> = ({ caption, onOpenCaptionCoach, disabled }) => {
  const { coachId } = useSocialCoachSession();
  const {
    onBehalfOf,
    effectivePlayerId,
    defaultMessage,
    message,
    setMessage,
    setMessageTouched,
    messageTouched,
    resetMessageTouched,
    applyGeneratedCaption,
    blockedWords,
    flaggedWords,
    setBlockedWords,
    setFlaggedWords,
  } = usePostFlowContext();
  // On behalf of a player, their compliance words apply, not the session's.
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker(
    onBehalfOf ? { coachId: coachId ?? undefined, playerId: effectivePlayerId } : undefined
  );

  // The flow reads its starting caption once, so a caption that lands later (or a rewrite) comes in here.
  React.useEffect(() => {
    if (caption?.status === "ready") applyGeneratedCaption(caption.caption);
  }, [caption?.status, caption?.caption]);

  React.useEffect(() => {
    setBlockedWords(findBlockedWords(message || ""));
    setFlaggedWords(findFlaggedWords(message || ""));
  }, [message, findBlockedWords, findFlaggedWords]);

  const writing = !!caption && caption.status === "pending";
  const failed = !!caption && caption.status === "failed" && !defaultMessage;
  // A new caption is on its way and will replace the current one (an edited caption is kept, so no promise then).
  const rewriting = writing && !!defaultMessage && !messageTouched;
  const rewriteFailed = !!caption && caption.status === "failed" && !!defaultMessage;

  // A soft flash when a rewritten caption lands, so the change is noticed.
  const [justRewritten, setJustRewritten] = React.useState(false);
  const wasRewriting = React.useRef(false);
  React.useEffect(() => {
    if (wasRewriting.current && caption?.status === "ready") {
      setJustRewritten(true);
      const timer = setTimeout(() => setJustRewritten(false), 1500);
      wasRewriting.current = false;
      return () => clearTimeout(timer);
    }
    wasRewriting.current = rewriting;
    return undefined;
  }, [rewriting, caption?.status]);

  const rewrite = () => {
    resetMessageTouched();
    caption?.rewrite();
  };

  return (
    <div className="flex flex-col gap-2" data-testid="caption-field">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Caption</span>
        {caption && (
          <Button variant="ghost" size="sm" className="text-slate-600" onClick={rewrite} disabled={writing || disabled}>
            {rewriting ? <Spinner className="size-4" /> : <WandSparkles />}
            {rewriting ? "Rewriting…" : "Rewrite"}
          </Button>
        )}
        {!caption && onOpenCaptionCoach && (
          <Button variant="ghost" size="sm" className="text-slate-600" onClick={onOpenCaptionCoach} disabled={disabled}>
            <WandSparkles />
            Rewrite
          </Button>
        )}
      </div>

      {writing && !defaultMessage ? (
        <CaptionWriting />
      ) : (
        <>
          <DetectedPhraseAlert blockedWords={blockedWords} flaggedWords={flaggedWords} />
          {failed && (
            <p className="text-sm text-slate-500">
              We couldn't draft a caption. Write your own or try again.{" "}
              <Button variant="link" size="inline" onClick={rewrite}>
                Try again
              </Button>
            </p>
          )}
          {rewriteFailed && (
            <p className="text-sm text-slate-500">
              We couldn't rewrite it. Your caption is unchanged.{" "}
              <Button variant="link" size="inline" onClick={rewrite}>
                Try again
              </Button>
            </p>
          )}
          <div className="relative">
            <Textarea
              value={message ?? ""}
              onChange={event => {
                setMessageTouched(true);
                setMessage(event.target.value);
              }}
              placeholder="Write a caption..."
              aria-label="Caption"
              aria-busy={rewriting}
              readOnly={rewriting}
              // Starts at about four lines and grows with the caption, up to a cap.
              rows={4}
              className={cn(
                "min-h-24 max-h-60 resize-none field-sizing-content transition-shadow duration-500",
                rewriting && "text-slate-400",
                justRewritten && "ring-2 ring-primary/40"
              )}
              disabled={disabled}
            />
            {rewriting && <CaptionRewriting />}
          </div>
        </>
      )}
    </div>
  );
};
